// Sponsor Pool admin — promote/demote sponsored creators.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";

export function SponsorPoolTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("sponsored");
  const [busy, setBusy] = useState({});

  const load = () => api.get("/owner/users").then(({ data }) => setUsers(data || []));
  useEffect(() => { load(); }, []);

  const runAction = async (u, kind) => {
    setBusy((b) => ({ ...b, [u.id]: true }));
    try {
      await api.post(`/owner/${kind}-sponsored`, { user_id: u.id });
      toast.success(kind === "promote" ? `${u.display_name} promoted to Sponsored ★` : `${u.display_name} demoted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || `Failed to ${kind}`); }
    finally { setBusy((b) => ({ ...b, [u.id]: false })); }
  };

  const sponsored = users.filter((u) => u.is_sponsored);
  const candidates = users.filter((u) => !u.is_sponsored && (u.role === "creator" || u.role === "sponsor"));

  let visible = filter === "sponsored" ? sponsored : filter === "candidates" ? candidates : users;
  if (q.trim()) {
    const ql = q.trim().toLowerCase();
    visible = visible.filter((u) =>
      (u.display_name || "").toLowerCase().includes(ql) ||
      (u.email || "").toLowerCase().includes(ql) ||
      (u.id || "").toLowerCase().includes(ql)
    );
  }

  return (
    <div className="space-y-5" data-testid="sponsor-pool-tab">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="bg-nexus-surface p-4 border border-nexus-gold/30">
          <div className="label-eyebrow text-nexus-gold">// promoted</div>
          <div className="font-display text-3xl mt-1" data-testid="sp-count-sponsored">{sponsored.length}</div>
          <div className="text-[10px] text-white/40 mt-1">live in landing spotlight</div>
        </div>
        <div className="bg-nexus-surface p-4 border border-white/10">
          <div className="label-eyebrow">// candidates</div>
          <div className="font-display text-3xl mt-1" data-testid="sp-count-candidates">{candidates.length}</div>
          <div className="text-[10px] text-white/40 mt-1">creators / sponsors eligible</div>
        </div>
        <div className="bg-nexus-surface p-4 border border-white/10">
          <div className="label-eyebrow">// total users</div>
          <div className="font-display text-3xl mt-1" data-testid="sp-count-total">{users.length}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email, or id…"
          className="flex-1 min-w-[220px] bg-transparent border border-white/10 px-3 py-2 text-sm"
          data-testid="sp-search"
        />
        <div className="flex gap-1 border border-white/10 p-0.5">
          {[
            { id: "sponsored", label: `Promoted (${sponsored.length})` },
            { id: "candidates", label: `Candidates (${candidates.length})` },
            { id: "all", label: `All (${users.length})` },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`sp-filter-${f.id}`}
              className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-display transition-colors ${filter === f.id ? "bg-nexus-gold text-nexus-obsidian" : "text-white/60 hover:text-white"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-px bg-white/5">
        {visible.length === 0 && (
          <div className="bg-nexus-surface p-6 text-center text-white/40 text-sm">No users match this view.</div>
        )}
        {visible.map((u) => (
          <div key={u.id} className="bg-nexus-surface p-4 flex items-center gap-4 flex-wrap" data-testid={`sp-row-${u.id}`}>
            <img src={u.avatar} alt="" className="w-10 h-10 ring-1 ring-white/20" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium flex items-center gap-2">
                {u.display_name}
                {u.is_sponsored && <span className="bg-nexus-gold text-nexus-obsidian px-1.5 py-0.5 text-[8px] tracking-[0.3em] font-display font-black uppercase">★ Sponsored</span>}
              </div>
              <div className="text-[11px] text-white/40">{u.email} · {u.role} · L{u.level || 1} · {(u.wallet_balance || 0).toFixed(0)} NX</div>
            </div>
            {u.is_sponsored ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button disabled={busy[u.id]} className="btn-ghost !py-1.5 !px-3 text-[9px] !border-nexus-crimson !text-nexus-crimson disabled:opacity-50" data-testid={`sp-demote-${u.id}`}>{busy[u.id] ? "…" : "Demote"}</button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid={`sp-demote-dialog-${u.id}`}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove sponsorship</AlertDialogTitle>
                    <AlertDialogDescription>{u.display_name} will be dropped from the rotating Sponsored Spotlight and lose the free Studio perk.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`sp-demote-cancel-${u.id}`}>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => runAction(u, "demote")} data-testid={`sp-demote-go-${u.id}`}>Demote</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button disabled={busy[u.id]} className="btn-ghost !py-1.5 !px-3 text-[9px] !border-nexus-gold !text-nexus-gold disabled:opacity-50" data-testid={`sp-promote-${u.id}`}>{busy[u.id] ? "…" : "Promote ★"}</button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid={`sp-promote-dialog-${u.id}`}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Promote to Sponsored</AlertDialogTitle>
                    <AlertDialogDescription>{u.display_name} will join the Sponsored Spotlight on the Landing page and unlock free Studio tier.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`sp-promote-cancel-${u.id}`}>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => runAction(u, "promote")} data-testid={`sp-promote-go-${u.id}`}>Promote ★</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
